import { useDialogStore } from "@shared/common/contexts/dialog/useDialogStore";
import { sleep } from "@shared/common/utils/timing";
import { Dialog, DialogContentContainer, ScrollContainer } from "@shared/EDS/components/Dialog";
import { MessageBar } from "@shared/EDS/components/MessageBar";
import { JSX, useState } from "react";

interface GenerateSolutionFailedDialogProps {
  readonly toHipConversionErrors: string[];
  readonly isOpen?: boolean; // For testing purposes only, otherwise leave undefined
  readonly dialogId: string;
}

export function GenerateSolutionFailedDialog({
  toHipConversionErrors,
  isOpen,
  dialogId,
}: GenerateSolutionFailedDialogProps): JSX.Element {
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const [copyIcon, setCopyIcon] = useState<"copy" | "check">("copy");

  const doCloseDialog = () => {
    closeDialog(dialogId);
  };

  const doCopyErrorToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(toHipConversionErrors.join("\n"));
      setCopyIcon("check");
      await sleep(2000);
      setCopyIcon("copy");
    } catch (error) {
      console.error("Failed to copy text: ", error);
    }
  };

  return (
    <Dialog
      dialogId={dialogId}
      title="Genereren knoopplan is mislukt"
      width="800px"
      isOpen={isOpen}
    >
      <DialogContentContainer $maxHeight="85svh" className="px-l py-0">
        <MessageBar messageBarType="error">
          Het genereren van het knoopplan is niet gestart door een of meer problemen
        </MessageBar>
        <h2 className="mt-l mb-0 border-b-medium border-default p-m text-m leading-l font-bold">
          Toelichting
        </h2>
        <ScrollContainer $height={"calc(85vh - 20px - (20px + 20px + 24px))"} className="p-0">
          <div className="flex flex-col">
            {[...new Set(toHipConversionErrors)].map((error) => (
              <div key={error} className="border-b border-default p-m leading-m">
                {error}
              </div>
            ))}
          </div>
        </ScrollContainer>
        <div className="flex justify-end gap-xs px-0 pt-spacer-5 pb-l [&>button]:px-spacer-5 [&>button]:py-xs">
          <tractie-button
            data-testid="generate-solution-failed-copy-button"
            level="tertiary"
            onClick={() => {
              void doCopyErrorToClipboard();
            }}
          >
            <tractie-icon name={copyIcon} />
            Kopieer naar klembord
          </tractie-button>
          <tractie-button
            data-testid="generate-solution-failed-close-button"
            level="primary"
            onClick={doCloseDialog}
          >
            Sluiten
          </tractie-button>
        </div>
      </DialogContentContainer>
    </Dialog>
  );
}
